import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import PushToggle from '../components/PushToggle';
import { FileText, Clock, CheckCircle, XCircle, AlertCircle, ArrowRight, MessageSquare, Phone, Mail, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import TaxDocumentUpload from '../components/TaxDocumentUpload';
import RazorpayPayButton from '../components/RazorpayPayButton';
import CaseChatThread from '../components/CaseChatThread';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [applications, setApplications] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [taxCases, setTaxCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };
    try {
      const [appsRes, enqRes, taxRes] = await Promise.all([
        axios.get(`${API}/applications`, { headers }),
        axios.get(`${API}/enquiries/my`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/tax-cases/my`, { headers }).catch(() => ({ data: [] })),
      ]);

      const apps = appsRes.data || [];
      setApplications(apps);
      setEnquiries(enqRes.data || []);
      setTaxCases(taxRes.data || []);

      setStats({
        total: apps.length,
        pending: apps.filter(a => a.status === 'pending').length,
        approved: apps.filter(a => a.status === 'approved').length,
        rejected: apps.filter(a => a.status === 'rejected').length
      });
    } catch (error) {
      toast.error('Failed to load your dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={18} />;
      case 'under_review':
        return <AlertCircle size={18} />;
      case 'approved':
        return <CheckCircle size={18} />;
      case 'rejected':
        return <XCircle size={18} />;
      default:
        return <Clock size={18} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      {/* Dashboard Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '4rem 2rem' }}>
        <div style={{ marginBottom: '3rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: '#0F172A' }}>
              My Applications
            </h1>
            <p style={{ fontSize: '1.125rem', color: '#475569' }}>
              Track and manage all your applications in one place
            </p>
          </div>
          <PushToggle />
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
          <div className="stat-card" data-testid="stat-total-applications">
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem', fontWeight: '600' }}>Total Applications</div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#0F172A' }}>{stats.total}</div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #F59E0B' }} data-testid="stat-pending-applications">
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem', fontWeight: '600' }}>Pending</div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#92400E' }}>{stats.pending}</div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #FF6B35' }} data-testid="stat-approved-applications">
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem', fontWeight: '600' }}>Approved</div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#065F46' }}>{stats.approved}</div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #EF4444' }} data-testid="stat-rejected-applications">
            <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem', fontWeight: '600' }}>Rejected</div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#991B1B' }}>{stats.rejected}</div>
          </div>
        </div>

        {/* Applications List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ color: '#FF6B35', fontSize: '1.125rem' }}>Loading applications...</div>
          </div>
        ) : applications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', backgroundColor: 'white', borderRadius: '1.5rem', border: '1px solid #E2E8F0' }}>
            <FileText size={64} style={{ color: '#E2E8F0', margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0F172A' }}>No Applications Yet</h3>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>Start your financial journey by applying for a service</p>
            <button 
              onClick={() => navigate('/services')}
              className="primary-button"
              data-testid="browse-services-button"
            >
              Browse Services
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {applications.map((app) => (
              <div 
                key={app.id} 
                className="service-card" 
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}
                data-testid={`application-card-${app.id}`}
              >
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <span className={`status-badge status-${app.status}`}>
                      {getStatusIcon(app.status)}
                      {app.status.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#0F172A' }}>
                    {app.service_name}
                  </h3>
                  
                  <div style={{ color: '#475569', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong>Bank:</strong> {app.bank_name}
                  </div>
                  
                  {app.amount && (
                    <div style={{ color: '#475569', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                      <strong>Amount:</strong> ₹{app.amount.toLocaleString()}
                    </div>
                  )}
                  
                  {app.tenure_months && (
                    <div style={{ color: '#475569', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                      <strong>Tenure:</strong> {app.tenure_months} months
                    </div>
                  )}
                  
                  <div style={{ color: '#16A34A', fontSize: '0.875rem', marginTop: '0.75rem' }}>
                    Applied on {formatDate(app.created_at)}
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-end' }}>
                  <div style={{ fontSize: '0.875rem', color: '#475569' }}>
                    App ID: <span style={{ fontFamily: 'JetBrains Mono', fontWeight: '600' }}>{app.id.slice(0, 8)}</span>
                  </div>
                  <button 
                    onClick={() => navigate(`/apply/${app.service_id}`)}
                    className="outline-button"
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    data-testid={`view-details-${app.id}`}
                  >
                    View Details <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My Tax Cases Section */}
        <div style={{ marginTop: '4rem', marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.25rem', color: '#0F172A' }} data-testid="my-tax-cases-heading">
              My Tax Cases
            </h2>
            <p style={{ fontSize: '1rem', color: '#475569' }}>
              ITR, GST, TDS & business registration requests.
            </p>
          </div>
          <button onClick={() => navigate('/tax-services')} className="primary-button" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }} data-testid="new-tax-case-button">
            <Receipt size={16} /> New Tax Service
          </button>
        </div>

        {loading ? null : taxCases.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 2rem', backgroundColor: 'white', borderRadius: '1.5rem', border: '1px dashed #E2E8F0' }} data-testid="no-tax-cases">
            <Receipt size={48} style={{ color: '#E2E8F0', margin: '0 auto 0.75rem' }} />
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.4rem', color: '#0F172A' }}>No tax cases yet</h3>
            <p style={{ color: '#475569', marginBottom: '1.25rem', fontSize: '0.95rem' }}>File your ITR or GST in 24 hours with an expert CA.</p>
            <button onClick={() => navigate('/tax-services')} className="outline-button" data-testid="start-tax-case-button">
              Browse Tax Services <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }} data-testid="tax-cases-list">
            {taxCases.map((tc) => (
              <div
                key={tc.id}
                className="service-card"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}
                data-testid={`tax-case-card-${tc.id}`}
              >
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                    <span className={`status-badge status-${tc.status === 'new' ? 'pending' : tc.status === 'completed' || tc.status === 'filed' ? 'approved' : tc.status === 'cancelled' ? 'rejected' : 'pending'}`}>
                      {tc.status.replace('_', ' ')}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Ref: <span style={{ fontFamily: 'monospace', color: '#10B981', fontWeight: 600 }}>{tc.id.slice(0, 8)}</span></span>
                  </div>
                  <h3 style={{ fontSize: '1.15rem', marginBottom: '0.35rem', color: '#0F172A' }}>{tc.service_type}</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem', color: '#475569', fontSize: '0.875rem', marginTop: '0.4rem' }}>
                    {tc.quoted_amount ? <div><strong>Quoted:</strong> ₹{Number(tc.quoted_amount).toLocaleString('en-IN')}</div> : null}
                    {tc.city ? <div><strong>City:</strong> {tc.city}</div> : null}
                    {tc.pan ? <div><strong>PAN:</strong> {tc.pan}</div> : null}
                  </div>
                  {tc.quoted_amount && tc.payment_status !== 'paid' ? (
                    <div style={{ marginTop: '0.85rem', padding: '0.85rem 1rem', background: 'linear-gradient(135deg, #FFF7ED 0%, #FED7AA 100%)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ fontSize: '0.85rem', color: '#9A3412' }}>
                        <strong>Pay now</strong> to fast-track your filing with a verified CA.
                      </div>
                      <RazorpayPayButton
                        entityType="tax_case"
                        entityId={tc.id}
                        amountInr={tc.quoted_amount}
                        prefill={{ name: tc.name, email: tc.email, contact: tc.phone }}
                        onPaid={() => fetchAll()}
                        variant="compact"
                      />
                    </div>
                  ) : null}
                  {tc.payment_status === 'paid' ? (
                    <div style={{ marginTop: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.8rem', background: '#ECFDF5', color: '#065F46', borderRadius: 999, fontSize: '0.8rem', fontWeight: 700, border: '1px solid #A7F3D0' }} data-testid={`tax-case-paid-${tc.id}`}>
                      <CheckCircle size={12} /> Paid
                    </div>
                  ) : null}
                  {tc.admin_notes ? (
                    <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', background: '#ECFDF5', borderLeft: '3px solid #10B981', borderRadius: 6, color: '#065F46', fontSize: '0.85rem' }}>
                      <strong>Expert note:</strong> {tc.admin_notes}
                    </div>
                  ) : null}
                  <div style={{ color: '#16A34A', fontSize: '0.825rem', marginTop: '0.75rem' }}>
                    Submitted on {formatDate(tc.created_at)}
                  </div>
                  <TaxDocumentUpload caseId={tc.id} />
                  <CaseChatThread caseId={tc.id} currentRole="customer" testIdPrefix="customer-chat" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My Enquiries Section */}
        <div style={{ marginTop: '4rem', marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.25rem', color: '#0F172A' }} data-testid="my-enquiries-heading">
              My Enquiries
            </h2>
            <p style={{ fontSize: '1rem', color: '#475569' }}>
              Quick enquiries you've submitted — our advisor will call you back.
            </p>
          </div>
          <button onClick={() => navigate('/enquiry')} className="primary-button" data-testid="new-enquiry-button">
            <MessageSquare size={16} /> New Enquiry
          </button>
        </div>

        {loading ? null : enquiries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 2rem', backgroundColor: 'white', borderRadius: '1.5rem', border: '1px dashed #E2E8F0' }} data-testid="no-enquiries">
            <MessageSquare size={48} style={{ color: '#E2E8F0', margin: '0 auto 0.75rem' }} />
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.4rem', color: '#0F172A' }}>No enquiries yet</h3>
            <p style={{ color: '#475569', marginBottom: '1.25rem', fontSize: '0.95rem' }}>Tell us what you need — we'll call back within 24 hours.</p>
            <button onClick={() => navigate('/enquiry')} className="outline-button" data-testid="start-enquiry-button">
              Start an Enquiry <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }} data-testid="enquiries-list">
            {enquiries.map((enq) => (
              <div
                key={enq.id}
                className="service-card"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}
                data-testid={`enquiry-card-${enq.id}`}
              >
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <span className={`status-badge status-${enq.status === 'new' ? 'pending' : enq.status === 'converted' ? 'approved' : enq.status === 'closed' ? 'rejected' : 'pending'}`}>
                      {enq.status === 'new' ? <Clock size={14} /> : enq.status === 'contacted' ? <Phone size={14} /> : enq.status === 'qualified' ? <CheckCircle size={14} /> : enq.status === 'converted' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {enq.status.replace('_', ' ')}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Ref: <span style={{ fontFamily: 'monospace', color: '#FF6B35', fontWeight: 600 }}>{enq.id.slice(0, 8)}</span></span>
                  </div>
                  <h3 style={{ fontSize: '1.15rem', marginBottom: '0.35rem', color: '#0F172A' }}>
                    {enq.service_interested}
                    {enq.bank_preference ? <span style={{ color: '#64748B', fontWeight: 400, fontSize: '0.95rem' }}>  •  {enq.bank_preference}</span> : null}
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem', color: '#475569', fontSize: '0.875rem', marginTop: '0.4rem' }}>
                    {enq.loan_amount ? <div><strong>Amount:</strong> ₹{Number(enq.loan_amount).toLocaleString('en-IN')}</div> : null}
                    {enq.city ? <div><strong>City:</strong> {enq.city}</div> : null}
                    {enq.employment_type ? <div><strong>Employment:</strong> {enq.employment_type}</div> : null}
                  </div>
                  {enq.admin_notes ? (
                    <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', background: '#FFF7ED', borderLeft: '3px solid #FF6B35', borderRadius: 6, color: '#7C2D12', fontSize: '0.85rem' }}>
                      <strong>Advisor note:</strong> {enq.admin_notes}
                    </div>
                  ) : null}
                  <div style={{ color: '#16A34A', fontSize: '0.825rem', marginTop: '0.75rem' }}>
                    Submitted on {formatDate(enq.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', minWidth: 180 }}>
                  <div style={{ fontSize: '0.825rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={13} /> {enq.phone}</div>
                  <div style={{ fontSize: '0.825rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} /> {enq.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
